import {
    formatOrderPriceRupees,
    formatOrderTypeLabel,
    type ModifySnapshotDisplay,
    type OrderPreview,
} from './domains/tradingDomain'

const LTP_MESSAGE = /^Last traded price\s/i
const POSITION_MESSAGE = /^Current position:/i
const MARGIN_MESSAGE = /^Available margin\s/i

export function parseLtpRupeesFromPreviewMessages(messages: string[]): number | null {
    for (const message of messages) {
        const match = message.match(/Last traded price\s+(₹[\d,]+(?:\.\d+)?)/i)
        if (match?.[1]) {
            const numeric = Number(match[1].replace(/[₹,]/g, ''))
            if (Number.isFinite(numeric) && numeric > 0) {
                return numeric
            }
        }
    }
    return null
}

export function partitionPreviewMessages(messages: string[]): {
    warnings: string[]
    detailLines: string[]
} {
    const warnings: string[] = []
    const detailLines: string[] = []
    for (const message of messages) {
        if (LTP_MESSAGE.test(message)) {
            continue
        }
        if (POSITION_MESSAGE.test(message) || MARGIN_MESSAGE.test(message)) {
            detailLines.push(message.replace(/\.$/, ''))
            continue
        }
        if (
            message.includes('already have an open') ||
            message.includes('currently have') ||
            message.includes("can't") ||
            message.toLowerCase().includes('cannot')
        ) {
            warnings.push(message)
        } else {
            detailLines.push(message.replace(/\.$/, ''))
        }
    }
    return { warnings, detailLines }
}

export type ModifyFieldChange = {
    label: string
    fromLabel: string
    toLabel: string
}

export function getModifyFieldChanges(
    current: ModifySnapshotDisplay,
    proposed: ModifySnapshotDisplay,
): ModifyFieldChange[] {
    const changes: ModifyFieldChange[] = []
    if (current.quantity !== proposed.quantity) {
        changes.push({
            label: 'Quantity',
            fromLabel: String(current.quantity),
            toLabel: String(proposed.quantity),
        })
    }
    if (current.orderTypeLabel !== proposed.orderTypeLabel) {
        changes.push({
            label: 'Order type',
            fromLabel: current.orderTypeLabel,
            toLabel: proposed.orderTypeLabel,
        })
    }
    const currentLimit = current.limitPriceLabel ?? '—'
    const proposedLimit = proposed.limitPriceLabel ?? '—'
    if (currentLimit !== proposedLimit) {
        changes.push({
            label: 'Limit',
            fromLabel: currentLimit,
            toLabel: proposedLimit,
        })
    }
    const currentTrigger = current.triggerPriceLabel ?? '—'
    const proposedTrigger = proposed.triggerPriceLabel ?? '—'
    if (currentTrigger !== proposedTrigger) {
        changes.push({
            label: 'Trigger',
            fromLabel: currentTrigger,
            toLabel: proposedTrigger,
        })
    }
    return changes
}

export function formatPnlLine(amount: number, percent: number | null): string {
    const sign = amount >= 0 ? '+' : '−'
    const value = formatOrderPriceRupees(Math.abs(amount))
    if (percent != null && Number.isFinite(percent)) {
        const pctSign = percent >= 0 ? '+' : '−'
        return `${sign}${value}  (${pctSign}${Math.abs(percent).toFixed(2)}%)`
    }
    return `${sign}${value}`
}

export function estimateOrderValueRupees(preview: OrderPreview, ltpRupees: number | null): number | null {
    const qty = preview.quantity
    if (qty <= 0) return null
    const typeKey = preview.orderType.trim().toUpperCase().replace(/-/g, '_')
    if (typeKey === 'LIMIT' || typeKey === 'SL') {
        if (preview.limitPrice != null && preview.limitPrice > 0) {
            return qty * preview.limitPrice
        }
    }
    if (typeKey === 'MARKET' && ltpRupees != null) {
        return qty * ltpRupees
    }
    if ((typeKey === 'SL_M' || typeKey === 'SL') && preview.triggerPrice != null) {
        return qty * preview.triggerPrice
    }
    return null
}

export function formatCancelOrderOneLiner(
    side: string,
    quantity: number,
    orderTypeLabel: string,
    limitPriceLabel?: string,
    triggerPriceLabel?: string,
    statusLabel?: string,
): string {
    const typeUpper = orderTypeLabel.toUpperCase()
    let pricePart = orderTypeLabel
    if (limitPriceLabel) {
        pricePart = limitPriceLabel
    } else if (triggerPriceLabel) {
        pricePart = `Trigger ${triggerPriceLabel}`
    } else if (typeUpper === 'MARKET') {
        pricePart = 'Market'
    }
    const statusPart = statusLabel ? ` · ${statusLabel.replace(/_/g, ' ')}` : ''
    const needsPriceInMiddle = limitPriceLabel || triggerPriceLabel || typeUpper === 'MARKET'
    return needsPriceInMiddle
        ? `${side.toUpperCase()} ${quantity} · ${orderTypeLabel} ${pricePart}${statusPart}`
        : `${side.toUpperCase()} ${quantity} · ${orderTypeLabel}${statusPart}`
}
